import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";

const circleButtonSx = {
  width: 56,
  height: 56,
  border: "1px solid",
  borderColor: "divider",
};

/**
 * POS cart quantity dialog: +/- stepper that can go to 0.
 * At qty > 0 the CTA is UPDATE QUANTITY; at 0 it becomes a red REMOVE ITEM.
 */
export default function PosQuantityDialog({ open, title, quantity, onClose, onUpdate, onRemove }) {
  const [draftQty, setDraftQty] = useState(+quantity || 0);

  useEffect(() => {
    if (open) {
      setDraftQty(+quantity || 0);
    }
  }, [open, quantity]);

  const isRemove = draftQty === 0;

  const handleDecrement = () => {
    setDraftQty((q) => Math.max(0, q - 1));
  };

  const handleIncrement = () => {
    setDraftQty((q) => q + 1);
  };

  const handlePrimary = () => {
    if (isRemove) {
      onRemove();
    } else {
      onUpdate(draftQty);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
        }}
      >
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="textSecondary">
            x {+quantity || 0}
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {/* MUI zeroes padding-top on a DialogContent that follows a DialogTitle */}
      <DialogContent sx={{ "&.MuiDialogContent-root": { pt: 2 } }}>
        <Box
          py={3}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}
        >
          <IconButton
            aria-label="decrease quantity"
            onClick={handleDecrement}
            disabled={draftQty <= 0}
            sx={circleButtonSx}
          >
            <RemoveIcon fontSize="large" />
          </IconButton>

          <Typography
            sx={{
              fontSize: 64,
              fontWeight: 700,
              lineHeight: 1,
              minWidth: 80,
              textAlign: "center",
              userSelect: "none",
            }}
          >
            {draftQty}
          </Typography>

          <IconButton aria-label="increase quantity" onClick={handleIncrement} sx={circleButtonSx}>
            <AddIcon fontSize="large" />
          </IconButton>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color={isRemove ? "error" : "primary"}
          onClick={handlePrimary}
        >
          {isRemove ? "REMOVE ITEM" : "UPDATE QUANTITY"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
